<template>
  <section>
    <div class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div class="text-start">
        <h2 class="text-xl font-bold text-gray-800">
          Empresas visitadas
        </h2>

        <p class="mt-1 text-sm text-gray-500">
          Cadastre os clientes que serão usados na geração dos relatórios técnicos.
        </p>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ReportSearchBar
          :model-value="search"
          placeholder="Buscar por empresa, CNPJ, cidade..."
          @update:model-value="setSearch"
        />

        <div class="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="rounded-lg px-3 py-2 text-sm transition"
            :class="
              statusFilter === option.value
                ? 'bg-white text-[#2f8f2f] shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            "
            @click="statusFilter = option.value"
          >
            {{ option.label }}
          </button>
        </div>

        <ReportViewModeToggle
          :model-value="viewMode"
          @update:model-value="setViewMode"
        />

        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
          @click="openCreateDrawer"
        >
          <PhPlus class="h-5 w-5" />
          Nova empresa
        </button>
      </div>
    </div>

    <ReportVisitClientTable
      v-if="viewMode === 'table'"
      :clients="filteredClients"
      @edit="openEditDrawer"
      @disable="disableClient"
      @enable="enableClient"
    />

    <ReportVisitClientCards
      v-else
      :clients="filteredClients"
      @edit="openEditDrawer"
      @disable="disableClient"
      @enable="enableClient"
    />

    <ReportVisitClientFormDrawer
      v-model="isDrawerOpen"
      :client="selectedClient"
      :loading="isSaving"
      @submit="saveClient"
      @close="clearSelectedClient"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { PhPlus } from "@phosphor-icons/vue";
import { showToast as toast } from "@/helpers/UseToasts";

import { dataReport } from "../../store";
import ReportVisitClientsService from "../../services/reportVisitClients.service";

import ReportSearchBar from "../shared/ReportSearchBar.vue";
import ReportViewModeToggle from "../shared/ReportViewModeToggle.vue";
import ReportVisitClientTable from "../clients/ReportVisitClientTable.vue";
import ReportVisitClientCards from "../clients/ReportVisitClientCards.vue";
import ReportVisitClientFormDrawer from "../clients/ReportVisitClientFormDrawer.vue";

const USE_API = true;

const isDrawerOpen = ref(false);
const isSaving = ref(false);
const selectedClient = ref(null);
const statusFilter = ref("all");

const statusOptions = [
  {
    label: "Todas",
    value: "all",
  },
  {
    label: "Ativas",
    value: "active",
  },
  {
    label: "Inativas",
    value: "inactive",
  },
];

const search = computed(() => dataReport.state.filters.visitClientsSearch);
const viewMode = computed(() => dataReport.state.visitClientsViewMode);
const clients = computed(() => dataReport.state.visitClients);

const filteredClients = computed(() => {
  const term = search.value.toLowerCase().trim();

  return clients.value.filter((client) => {
    const matchesStatus =
      statusFilter.value === "all" ||
      (statusFilter.value === "active" && client.is_active) ||
      (statusFilter.value === "inactive" && !client.is_active);

    if (!matchesStatus) return false;

    if (!term) return true;

    return [
      client.corporate_name,
      client.trade_name,
      client.cnpj,
      client.responsible_name,
      client.responsible_role,
      client.city,
      client.state,
      client.phone,
      client.email,
    ]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });
});

onMounted(() => {
  loadClients();
});

async function loadClients() {
  dataReport.commit("setVisitClientsLoading", true);

  try {
    if (!USE_API) {
      dataReport.commit("setVisitClients", getMockClients());
      return;
    }

    const response = await ReportVisitClientsService.getAll({
      include_inactive: true,
    });

    const responseData =
      response?.data?.data ||
      response?.data?.clients ||
      response?.data ||
      [];

    dataReport.commit("setVisitClients", responseData);
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar as empresas visitadas.",
      "error"
    );
  } finally {
    dataReport.commit("setVisitClientsLoading", false);
  }
}

function setSearch(value) {
  dataReport.commit("setVisitClientsSearch", value);
}

function setViewMode(mode) {
  dataReport.commit("setVisitClientsViewMode", mode);
}

function openCreateDrawer() {
  selectedClient.value = null;
  dataReport.commit("setSelectedVisitClient", null);
  isDrawerOpen.value = true;
}

function openEditDrawer(client) {
  selectedClient.value = { ...client };
  dataReport.commit("setSelectedVisitClient", client);
  isDrawerOpen.value = true;
}

function clearSelectedClient() {
  selectedClient.value = null;
  dataReport.commit("setSelectedVisitClient", null);
}

async function saveClient({ payload, logoFile, logoPreview, isEdit }) {
  isSaving.value = true;

  try {
    let savedLogoPath = payload.logo_path;
    let savedLogoUrl = payload.logo_url;

    if (!USE_API && logoFile && logoPreview) {
      savedLogoUrl = logoPreview;
      savedLogoPath = "";
    }

    if (USE_API && logoFile) {
      const logoResponse = payload.logo_path
        ? await ReportVisitClientsService.replaceLogo(logoFile, payload.logo_path)
        : await ReportVisitClientsService.saveLogo(logoFile);

      savedLogoPath =
        logoResponse?.data ||
        logoResponse?.data?.path ||
        logoResponse?.data?.file_path ||
        logoResponse?.data?.data?.path ||
        "";

      savedLogoUrl = process.env.VUE_APP_IMAGE_SERVE.concat('storage/').concat(
        logoResponse?.data ||
        logoResponse?.data?.url ||
        logoResponse?.data?.file_url ||
        logoResponse?.data?.data?.url ||
        "");
    }

    const finalPayload = {
      ...payload,
      logo_path: savedLogoPath,
      logo_url: savedLogoUrl,
    };

    if (!USE_API) {
      saveClientMock(finalPayload, isEdit);
    } else if (isEdit) {
      const response = await ReportVisitClientsService.update(
        finalPayload.id,
        finalPayload
      );

      const updatedClient = response?.data?.data || response?.data || finalPayload;

      dataReport.commit("updateVisitClient", updatedClient);
    } else {
      const response = await ReportVisitClientsService.create(finalPayload);

      const createdClient = response?.data?.data || response?.data || finalPayload;

      dataReport.commit("addVisitClient", createdClient);
    }

    toast(
      "Protege informa:",
      isEdit
        ? "Empresa atualizada com sucesso."
        : "Empresa cadastrada com sucesso.",
      "success"
    );

    isDrawerOpen.value = false;
    clearSelectedClient();
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível salvar a empresa. Verifique os dados e tente novamente.",
      "error"
    );
  } finally {
    isSaving.value = false;
  }
}

function saveClientMock(payload, isEdit) {
  if (isEdit) {
    dataReport.commit("updateVisitClient", payload);
    return;
  }

  const newClient = {
    ...payload,
    id: Date.now(),
    company_id: localStorage.getItem("__company"),
    is_active: true,
    created_at: new Date().toISOString(),
  };

  dataReport.commit("addVisitClient", newClient);
}

async function disableClient(client) {
  const name = client.trade_name || client.corporate_name || "esta empresa";

  const confirmDisable = window.confirm(
    `Deseja realmente inativar ${name}?`
  );

  if (!confirmDisable) return;

  try {
    if (USE_API) {
      const response = await ReportVisitClientsService.disable(client.id);
      const updatedClient = response?.data?.data;

      if (updatedClient) {
        dataReport.commit("updateVisitClient", updatedClient);
      } else {
        dataReport.commit("disableVisitClient", client.id);
      }
    } else {
      dataReport.commit("disableVisitClient", client.id);
    }

    toast(
      "Protege informa:",
      "Empresa inativada com sucesso.",
      "success"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível inativar a empresa.",
      "error"
    );
  }
}

async function enableClient(client) {
  const name = client.trade_name || client.corporate_name || "esta empresa";

  const confirmEnable = window.confirm(
    `Deseja realmente reativar ${name}?`
  );

  if (!confirmEnable) return;

  try {
    if (USE_API) {
      const response = await ReportVisitClientsService.enable(client.id);
      const updatedClient = response?.data?.data;

      if (updatedClient) {
        dataReport.commit("updateVisitClient", updatedClient);
      } else {
        dataReport.commit("enableVisitClient", client.id);
      }
    } else {
      dataReport.commit("enableVisitClient", client.id);
    }

    toast(
      "Protege informa:",
      "Empresa reativada com sucesso.",
      "success"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível reativar a empresa.",
      "error"
    );
  }
}

function getMockClients() {
  return [
    {
      id: 1,
      company_id: localStorage.getItem("__company"),
      corporate_name: "Art Fritas Indústria e Comércio LTDA",
      trade_name: "Art Fritas",
      cnpj: "06.970.000/0001-00",
      responsible_name: "Responsável Técnico",
      responsible_role: "Gerência",
      phone: "(15) 99999-9999",
      email: "contato@artfritas.com.br",
      city: "Pilar do Sul",
      state: "SP",
      logo_path: "",
      logo_url: "",
      notes: "",
      is_active: true,
      created_at: "2026-05-05T10:00:00.000000Z",
    },
    {
      id: 2,
      company_id: localStorage.getItem("__company"),
      corporate_name: "Empresa Inativa Exemplo LTDA",
      trade_name: "Empresa Inativa",
      cnpj: "11.111.111/0001-11",
      responsible_name: "João Silva",
      responsible_role: "RH",
      phone: "(15) 97777-7777",
      email: "inativa@empresa.com.br",
      city: "Sorocaba",
      state: "SP",
      logo_path: "",
      logo_url: "",
      notes: "",
      is_active: false,
      created_at: "2026-05-05T11:00:00.000000Z",
    },
  ];
}
</script>